import { supabase } from '../lib/supabase';

/**
 * Núcleo documental propio de LDD ERP.
 * Contasimple queda como integración temporal, no como fuente de verdad.
 */

export const documentService = {
  async list(filters = {}) {
    let q = supabase
      .from('v_documentos_comerciales_operativa')
      .select('*')
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false });

    if (filters.tipo) q = q.eq('tipo', filters.tipo);
    if (filters.estado) q = q.eq('estado', filters.estado);
    if (filters.estadoCobro) q = q.eq('estado_cobro', filters.estadoCobro);
    if (filters.clienteId) q = q.eq('cliente_id', filters.clienteId);
    if (filters.clienteB2bId) q = q.eq('cliente_b2b_id', filters.clienteB2bId);
    if (filters.pedidoId) q = q.eq('pedido_id', filters.pedidoId);
    if (filters.desde) q = q.gte('fecha', filters.desde);
    if (filters.hasta) q = q.lte('fecha', filters.hasta);

    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },

  async getById(id) {
    const [docRes, linesRes, relationsRes, paymentsRes, integrationsRes] = await Promise.all([
      supabase
        .from('v_documentos_comerciales_operativa')
        .select('*')
        .eq('id', id)
        .single(),
      supabase
        .from('documento_comercial_lineas')
        .select('*')
        .eq('documento_id', id)
        .order('orden', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('documento_relaciones')
        .select('*')
        .or(`documento_origen_id.eq.${id},documento_destino_id.eq.${id}`),
      supabase
        .from('cobro_aplicaciones')
        .select('id,importe_aplicado,created_at,cobro:cobros(*)')
        .eq('documento_id', id)
        .order('created_at', { ascending: true }),
      supabase
        .from('documento_integraciones')
        .select('*')
        .eq('documento_id', id),
    ]);

    if (docRes.error) throw docRes.error;
    if (linesRes.error) throw linesRes.error;
    if (relationsRes.error) throw relationsRes.error;
    if (paymentsRes.error) throw paymentsRes.error;
    if (integrationsRes.error) throw integrationsRes.error;

    return {
      ...docRes.data,
      lineas: linesRes.data || [],
      relaciones: relationsRes.data || [],
      cobros: paymentsRes.data || [],
      integraciones: integrationsRes.data || [],
    };
  },

  async createDraft(payload) {
    const tipo = payload?.tipo || 'factura';

    const { data, error } = await supabase
      .from('documentos_comerciales')
      .insert({
        empresa_id: payload?.empresa_id || null,
        tipo,
        cliente_id: payload?.cliente_id || null,
        cliente_b2b_id: payload?.cliente_b2b_id || null,
        pedido_id: payload?.pedido_id || null,
        fecha: payload?.fecha || new Date().toISOString().slice(0, 10),
        fecha_vencimiento: payload?.fecha_vencimiento || null,
        moneda: payload?.moneda || 'EUR',
        estado: 'borrador',
        estado_cobro: ['factura', 'factura_rectificativa'].includes(tipo) ? 'pendiente' : 'no_aplica',
        forma_pago: payload?.forma_pago || null,
        notas: payload?.notas || null,
        condiciones: payload?.condiciones || null,
        origen: 'LDD_ERP',
      })
      .select('*')
      .single();

    if (error) throw error;
    return data;
  },

  async addLine(documentoId, line, orden = 0) {
    const { data, error } = await supabase
      .from('documento_comercial_lineas')
      .insert({
        documento_id: documentoId,
        pedido_linea_id: line?.pedido_linea_id || null,
        linea_origen_id: line?.linea_origen_id || null,
        tienda_producto_id: line?.tienda_producto_id || line?.producto_id || null,
        descripcion: line?.descripcion || line?.nombre || 'Producto',
        cantidad: Number(line?.cantidad || 0),
        unidad: line?.unidad || 'ud',
        precio_unitario: Number(line?.precio_unitario || 0),
        descuento_pct: Number(line?.descuento_pct || line?.descuento || 0),
        tipo_iva: Number(line?.tipo_iva ?? 0),
        recargo_pct: Number(line?.recargo_pct ?? 0),
        retencion_pct: Number(line?.retencion_pct ?? 0),
        coste_unitario_historico:
          line?.coste_unitario_historico == null
            ? null
            : Number(line.coste_unitario_historico),
        orden,
        metadata: line?.metadata || {},
      })
      .select('*')
      .single();

    if (error) throw error;
    return data;
  },

  async replaceLines(documentoId, lines = []) {
    const { error: delError } = await supabase
      .from('documento_comercial_lineas')
      .delete()
      .eq('documento_id', documentoId);

    if (delError) throw delError;

    if (!lines.length) return [];

    const rows = lines.map((line, index) => ({
      documento_id: documentoId,
      pedido_linea_id: line?.pedido_linea_id || null,
      linea_origen_id: line?.linea_origen_id || null,
      tienda_producto_id: line?.tienda_producto_id || line?.producto_id || null,
      descripcion: line?.descripcion || line?.nombre || 'Producto',
      cantidad: Number(line?.cantidad || 0),
      unidad: line?.unidad || 'ud',
      precio_unitario: Number(line?.precio_unitario || 0),
      descuento_pct: Number(line?.descuento_pct || line?.descuento || 0),
      tipo_iva: Number(line?.tipo_iva ?? 0),
      recargo_pct: Number(line?.recargo_pct ?? 0),
      retencion_pct: Number(line?.retencion_pct ?? 0),
      coste_unitario_historico:
        line?.coste_unitario_historico == null
          ? null
          : Number(line.coste_unitario_historico),
      orden: index,
      metadata: line?.metadata || {},
    }));

    const { data, error } = await supabase
      .from('documento_comercial_lineas')
      .insert(rows)
      .select('*');

    if (error) throw error;
    return data || [];
  },

  async updateDraft(id, patch) {
    const allowed = {
      empresa_id: patch?.empresa_id,
      cliente_id: patch?.cliente_id,
      cliente_b2b_id: patch?.cliente_b2b_id,
      pedido_id: patch?.pedido_id,
      fecha: patch?.fecha,
      fecha_vencimiento: patch?.fecha_vencimiento,
      forma_pago: patch?.forma_pago,
      notas: patch?.notas,
      condiciones: patch?.condiciones,
    };

    const payload = Object.fromEntries(
      Object.entries(allowed).filter(([, value]) => value !== undefined)
    );

    payload.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('documentos_comerciales')
      .update(payload)
      .eq('id', id)
      .eq('estado', 'borrador')
      .select('*')
      .single();

    if (error) throw error;
    return data;
  },

  async issue(id, serie = 'LDD') {
    const { data, error } = await supabase.rpc('emitir_documento', {
      p_documento_id: id,
      p_serie: serie,
    });

    if (error) throw error;
    return data;
  },

  async createFromDocument(sourceId, targetType, options = {}) {
    const { data, error } = await supabase.rpc('crear_documento_desde_origen', {
      p_documento_origen_id: sourceId,
      p_tipo_destino: targetType,
      p_fecha: options?.fecha || new Date().toISOString().slice(0, 10),
      p_fecha_vencimiento: options?.fecha_vencimiento || null,
    });

    if (error) throw error;
    return data;
  },

  async registerPayment(invoiceId, payment) {
    const { data, error } = await supabase.rpc('registrar_cobro_factura', {
      p_documento_id: invoiceId,
      p_importe: Number(payment?.importe || 0),
      p_fecha: payment?.fecha || new Date().toISOString().slice(0, 10),
      p_metodo: payment?.metodo || 'transferencia',
      p_referencia: payment?.referencia || null,
      p_notas: payment?.notas || null,
    });

    if (error) throw error;
    return data;
  },

  async cancelPayment(cobroId) {
    const { data, error } = await supabase
      .from('cobros')
      .update({
        estado: 'anulado',
        updated_at: new Date().toISOString(),
      })
      .eq('id', cobroId)
      .select('*')
      .single();

    if (error) throw error;
    return data;
  },

  async setIntegration(documentoId, provider, values = {}) {
    const { data, error } = await supabase
      .from('documento_integraciones')
      .upsert({
        documento_id: documentoId,
        proveedor: provider,
        external_id: values?.external_id || null,
        external_numero: values?.external_numero || null,
        external_status: values?.external_status || null,
        external_payload: values?.external_payload || {},
        last_sync_at: values?.last_sync_at || new Date().toISOString(),
        sync_error: values?.sync_error || null,
        activa: values?.activa ?? true,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'documento_id,proveedor' })
      .select('*')
      .single();

    if (error) throw error;
    return data;
  },
};

export default documentService;
